/**
 * Product Validation Service
 * Calculates channel-specific readiness status based on required fields
 */

/**
 * Validate if product is ready for Shopee export
 * @param {Object} product - Product object
 * @returns {Object} { ready: boolean, missingFields: string[] }
 */
export function validateShopeeReadiness(product) {
  const requiredFields = [
    { key: 'name', label: 'Nome do Produto' },
    { key: 'sku', label: 'SKU' },
    { key: 'price', label: 'Preço' },
    { key: 'cost', label: 'Custo' },
    { key: 'category', label: 'Categoria' },
    { key: 'shopeeCategory', label: 'Categoria Shopee' },
    { key: 'weight', label: 'Peso' },
    { key: 'width', label: 'Largura' },
    { key: 'height', label: 'Altura' },
    { key: 'length', label: 'Comprimento' },
    { key: 'stock', label: 'Estoque' },
    { key: 'images', label: 'Imagens', validator: (val) => Array.isArray(val) && val?.length > 0 },
    { key: 'desiredMargin', label: 'Margem Desejada' }
  ];

  const missingFields = [];

  for (const field of requiredFields) {
    const value = product?.[field?.key];
    
    if (field?.validator) {
      if (!field?.validator(value)) {
        missingFields?.push(field?.label);
      }
    } else {
      if (value === null || value === undefined || value === '') {
        missingFields?.push(field?.label);
      }
    }
  }

  return {
    ready: missingFields?.length === 0,
    missingFields
  };
}

/**
 * Validate if product is ready for Mercado Livre export
 * @param {Object} product - Product object
 * @returns {Object} { ready: boolean, missingFields: string[] }
 */
export function validateMercadoLivreReadiness(product) {
  const requiredFields = [
    { key: 'name', label: 'Nome do Produto' },
    { key: 'sku', label: 'SKU' },
    { key: 'price', label: 'Preço' },
    { key: 'cost', label: 'Custo' },
    { key: 'category', label: 'Categoria' },
    { key: 'mercadolivreCategory', label: 'Categoria Mercado Livre' },
    { key: 'mercadolivreListingType', label: 'Tipo de Anúncio' },
    { key: 'weight', label: 'Peso' },
    { key: 'width', label: 'Largura' },
    { key: 'height', label: 'Altura' },
    { key: 'length', label: 'Comprimento' },
    { key: 'stock', label: 'Estoque' },
    { key: 'images', label: 'Imagens', validator: (val) => Array.isArray(val) && val?.length > 0 },
    { key: 'ncm', label: 'NCM' },
    { key: 'desiredMargin', label: 'Margem Desejada' }
  ];

  const missingFields = [];

  for (const field of requiredFields) {
    const value = product?.[field?.key];
    
    if (field?.validator) {
      if (!field?.validator(value)) {
        missingFields?.push(field?.label);
      }
    } else {
      if (value === null || value === undefined || value === '') {
        missingFields?.push(field?.label);
      }
    }
  }

  return {
    ready: missingFields?.length === 0,
    missingFields
  };
}

/**
 * Validate if kit is ready for export
 * @param {Object} kit - Kit object
 * @returns {Object} { ready: boolean, missingFields: string[] }
 */
export function validateKitReadiness(kit) {
  const requiredFields = [
    { key: 'name', label: 'Nome do Kit' },
    { key: 'sku', label: 'SKU' },
    { key: 'price', label: 'Preço' },
    { key: 'cost', label: 'Custo' },
    { key: 'category', label: 'Categoria' },
    { key: 'images', label: 'Imagens', validator: (val) => Array.isArray(val) && val?.length > 0 }
  ];

  const missingFields = [];

  for (const field of requiredFields) {
    const value = kit?.[field?.key];
    
    if (field?.validator) {
      if (!field?.validator(value)) {
        missingFields?.push(field?.label);
      }
    } else {
      if (value === null || value === undefined || value === '') {
        missingFields?.push(field?.label);
      }
    }
  }

  return {
    ready: missingFields?.length === 0,
    missingFields
  };
}

/**
 * Calculate overall product readiness status
 * @param {Object} product - Product object
 * @returns {Object} { shopee: {...}, mercadolivre: {...}, kit: {...} }
 */
export function calculateProductReadiness(product) {
  const readiness = {
    shopee: validateShopeeReadiness(product),
    mercadolivre: validateMercadoLivreReadiness(product)
  };
  
  // Add kit validation if it's a kit
  if (product?.productType === 'kit') {
    readiness.kit = validateKitReadiness(product);
  }
  
  return readiness;
}

/**
 * Get status badge info for display
 * @param {boolean} ready - Readiness status
 * @returns {Object} { color, icon, text }
 */
export function getReadinessStatusBadge(ready) {
  if (ready) {
    return {
      color: 'bg-success/10 text-success border-success/20',
      icon: 'CheckCircle',
      text: 'Pronto'
    };
  }
  
  return {
    color: 'bg-warning/10 text-warning border-warning/20',
    icon: 'AlertCircle',
    text: 'Pendente'
  };
}
